/**
 * Vercel AI SDK adapter for codebase_search tool
 */

import { tool } from 'ai';
import { z } from 'zod';
import { executeCodebaseSearch } from './core.js';
import { CODEBASE_SEARCH_DESCRIPTION } from './prompts.js';
import type { CodebaseSearchConfig } from './types.js';

/**
 * Create Vercel AI SDK codebase_search tool
 * 
 * @param config - Configuration with repoId
 * @returns Vercel AI SDK tool definition (execution built-in)
 * 
 * @example
 * ```ts
 * import { generateText } from 'ai';
 * import { anthropic } from '@ai-sdk/anthropic';
 * import { createCodebaseSearchTool } from 'morphsdk/tools/codebase-search/vercel';
 * 
 * const tool = createCodebaseSearchTool({ repoId: 'my-project' });
 * 
 * const result = await generateText({
 *   model: anthropic('claude-3-5-sonnet-20241022'),
 *   tools: { codebaseSearch: tool },
 *   prompt: "Find authentication code",
 *   maxSteps: 5
 * });
 * 
 * // Vercel AI SDK automatically executes and handles results
 * console.log(result.text);
 * ```
 */
export function createCodebaseSearchTool(config: CodebaseSearchConfig) {
  const schema = z.object({
    query: z.string().describe('A complete question about what you want to understand. Ask as if talking to a colleague: "How does X work?", "What happens when Y?", "Where is Z handled?"'),
    target_directories: z.array(z.string()).describe('Prefix directory paths to limit search scope (single directory only, no glob patterns). Use [] to search entire repo.'),
    explanation: z.string().describe('One sentence explanation as to why this tool is being used, and how it contributes to the goal.'),
    limit: z.number().optional().describe('Max results to return (default: 10)'),
  });

  return tool({
    description: CODEBASE_SEARCH_DESCRIPTION,
    inputSchema: schema,
    execute: async (params) => {
      const { query, target_directories, explanation, limit } = params;
      const result = await executeCodebaseSearch(
        { query, target_directories, explanation, limit },
        config
      );

      if (!result.success) {
        return {
          error: result.error,
          results: [],
        };
      }

      // Format results for Vercel AI SDK
      return {
        found: result.results.length,
        searchTime: `${result.stats.searchTimeMs}ms`,
        results: result.results.map(r => ({
          file: r.filepath,
          symbol: r.symbolPath,
          lines: `${r.startLine}-${r.endLine}`,
          language: r.language,
          relevance: `${(r.rerankScore * 100).toFixed(1)}%`,
          code: r.content,
        })),
      };
    },
  });
}

/**
 * Get system prompt for Vercel AI SDK
 */
export function getSystemPrompt(): string {
  return CODEBASE_SEARCH_DESCRIPTION;
}

/**
 * Default export
 */
export default { createCodebaseSearchTool, getSystemPrompt };

